import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, ShoppingBag } from "lucide-react";
import { addToCart } from "@/lib/cart";
import { getConfig } from "@/lib/config";
import { apiFetch } from "@/lib/api";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const EASE = [0.22, 1, 0.36, 1];

const real = [
  { n: "01", name: "Shri Ganesh Murti", img: "/assets/ganesh-murti.png", alt: "Handcrafted white and gold Shri Ganesh murti by Laxwedeepan" },
  { n: "02", name: "Maa Durga Murti", img: "/assets/durga-murti-real.png", alt: "Handcrafted white and gold Maa Durga murti on her lion by Laxwedeepan" },
];

const slots = ["03", "04", "05", "06"];

export default function SacredEssentials() {
  const [catalog, setCatalog] = useState([]);
  const [checkoutOn, setCheckoutOn] = useState(false);
  useEffect(() => {
    apiFetch("/api/products").then(setCatalog).catch(() => {});
    getConfig().then((c) => setCheckoutOn(!!c.checkout_enabled));
  }, []);

  return (
    <main data-testid="essentials-page" className="pt-32 lg:pt-40">
      <div className="mx-auto max-w-7xl px-6 pb-16 text-center lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-gold"
        >
          पवित्र सामग्री
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
          className="mx-auto mt-6 max-w-3xl font-display text-4xl font-medium leading-tight text-brand-ink md:text-6xl"
        >
          Sacred <em className="italic text-brand-maroon">Essentials</em>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: EASE }}
          className="mx-auto mt-8 max-w-xl text-base font-light leading-relaxed text-brand-ink/70"
        >
          Real chavis from our own workshop — each murti handcrafted, painted and
          blessed before it leaves for your home. More offerings are being
          photographed and will grace this page soon.
        </motion.p>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-6 pb-24 sm:grid-cols-2 lg:grid-cols-3 lg:px-12">
        {real.map((r, i) => (
          <motion.div
            key={r.n}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: i * 0.1, ease: EASE }}
            className="group"
            data-testid={`essential-${r.n}`}
          >
            <div className="arch-frame border border-brand-border p-3 transition-colors duration-500 group-hover:border-brand-gold">
              <div className="arch-frame overflow-hidden">
                <img
                  src={r.img}
                  alt={r.alt}
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
            </div>
            <p className="mt-4 text-center font-display text-2xl text-brand-ink">{r.name}</p>
            {(() => {
              const match = catalog.find((c) => c.name === r.name && c.price != null && c.stock !== 0);
              return match && checkoutOn ? (
                <button
                  onClick={() => {
                    addToCart(match);
                    toast(`${r.name} added to your cart`, { description: "Checkout from the bag icon on top." });
                  }}
                  data-testid={`essential-order-${r.n}`}
                  className="mx-auto mt-3 flex w-fit items-center gap-2 rounded-full bg-brand-maroon px-5 py-2 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-ivory transition-colors duration-300 hover:bg-brand-gold"
                >
                  <ShoppingBag className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Add to Cart · ₹{match.price}
                </button>
              ) : (
                <a
                  href={`https://wa.me/919205511502?text=Namaste%20Laxwedeepan%2C%20I%20would%20like%20to%20order%20the%20${encodeURIComponent(r.name)}.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`essential-order-${r.n}`}
                  className="mx-auto mt-3 flex w-fit items-center gap-2 rounded-full border border-brand-gold px-5 py-2 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-maroon transition-colors duration-300 hover:bg-brand-gold hover:text-brand-ivory"
                >
                  <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Order This Murti
                </a>
              );
            })()}
          </motion.div>
        ))}
        {slots.map((n, i) => (
          <motion.div
            key={n}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: (real.length + i) * 0.08, ease: EASE }}
            className="arch-frame flex aspect-[4/5] flex-col items-center justify-center gap-3 border border-dashed border-brand-border bg-brand-cream/30 p-8 text-center"
            data-testid={`essential-slot-${n}`}
          >
            <span className="font-display text-5xl font-medium text-brand-border" aria-hidden="true">{n}</span>
            <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-brand-ink/40">
              Photograph arriving soon
            </p>
          </motion.div>
        ))}
      </div>

      <div className="mx-auto max-w-7xl px-6 pb-28 text-center lg:px-12">
        <a
          href="https://wa.me/919205511502?text=Namaste%20Laxwedeepan%2C%20I%20would%20like%20to%20enquire%20about%20your%20sacred%20essentials."
          target="_blank"
          rel="noopener noreferrer"
          data-testid="essentials-enquire-button"
          className="inline-flex items-center gap-3 rounded-full bg-brand-maroon px-8 py-4 text-[11px] font-medium uppercase tracking-[0.25em] text-brand-ivory transition-colors duration-300 hover:bg-brand-gold"
        >
          <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
          Enquire on WhatsApp
        </a>
      </div>
    </main>
  );
}
